import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import AddCardIcon from '@mui/icons-material/AddCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SmsIcon from '@mui/icons-material/Sms';
import GlassCard from '../ui/glass/components/GlassCard';
import { Loading } from '../components/common/Loading';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';

import AddDebtModal from '../components/modals/AddDebtModal';
import AddPaymentModal from '../components/modals/AddPaymentModal';
import PlanlanmisOdemeModal from '../components/modals/PlanlanmisOdemeModal';

// New Modular Components & Hook
import {
  useAccountStatement,
  AccountStatementTable,
  ScheduledPaymentsTable,
  AccountSummaryHeader
} from '../modules/account';

const CariHesapEkstresi = () => {
  const { id } = useParams();
  const {
    customer,
    loading,
    allMovements,
    openRecords,
    pendingScheduledPayments,
    finalBalance,
    handleSaveGeneralPayment,
    handleDeleteTransaction,
    handleDeleteScheduledPayment,
    handleSaveDebt
  } = useAccountStatement(id);

  const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPlanlamaModalOpen, setIsPlanlamaModalOpen] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return '---';
    try { return timestamp.toDate().toLocaleDateString('tr-TR'); } catch { return '---'; }
  };

  const formatCurrency = (amount) => (amount ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

  // Excel Export
  const handleExcelExport = () => {
    if (!allMovements || allMovements.length === 0) {
      alert('Dışa aktarılacak hareket bulunamadı.');
      return;
    }

    // Başlık satırları
    const headerRows = [
      { 'Tarih': 'HESAP EKSTRESİ', 'Açıklama': '', 'Borç': '', 'Alacak': '', 'Bakiye': '' },
      { 'Tarih': `Müşteri: ${customer.adSoyad}`, 'Açıklama': `Telefon: ${customer.telefon || '-'}`, 'Borç': '', 'Alacak': '', 'Bakiye': '' },
      { 'Tarih': `Oluşturma Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, 'Açıklama': '', 'Borç': '', 'Alacak': '', 'Bakiye': '' },
      { 'Tarih': '', 'Açıklama': '', 'Borç': '', 'Alacak': '', 'Bakiye': '' },
    ];

    // Hareket satırları
    const dataRows = allMovements.map(m => ({
      'Tarih': formatDate(m.date),
      'Açıklama': m.description || '-',
      'Borç': m.type === 'borc' ? (m.amount || 0) : '',
      'Alacak': m.type === 'alacak' ? (m.amount || 0) : '',
      'Bakiye': m.balance ?? 0
    }));

    // Alt toplam satırı
    const totalBorc = allMovements.filter(m => m.type === 'borc').reduce((s, m) => s + (m.amount || 0), 0);
    const totalAlacak = allMovements.filter(m => m.type === 'alacak').reduce((s, m) => s + (m.amount || 0), 0);
    const footerRows = [
      { 'Tarih': '', 'Açıklama': '', 'Borç': '', 'Alacak': '', 'Bakiye': '' },
      { 'Tarih': '', 'Açıklama': 'TOPLAM', 'Borç': totalBorc, 'Alacak': totalAlacak, 'Bakiye': finalBalance },
    ];

    const allData = [...headerRows, ...dataRows, ...footerRows];
    const worksheet = XLSX.utils.json_to_sheet(allData);

    // Sütun genişlikleri
    worksheet['!cols'] = [
      { wch: 15 }, // Tarih
      { wch: 50 }, // Açıklama
      { wch: 18 }, // Borç
      { wch: 18 }, // Alacak
      { wch: 18 }, // Bakiye
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Hesap Ekstresi');

    const safeFileName = (customer.adSoyad || 'Musteri').replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ ]/g, '').replace(/\s+/g, '_');
    XLSX.writeFile(workbook, `${safeFileName}_Hesap_Ekstresi.xlsx`);
  };

  // SMS Hatırlatma gönderimi
  const handleSendSmsReminder = async () => {
    if (!customer?.telefon) {
      alert('Müşterinin telefon numarası bulunamadı.');
      return;
    }
    if (finalBalance <= 0) {
      alert('Müşterinin borç bakiyesi bulunmuyor.');
      return;
    }
    setIsSendingSms(true);
    try {
      const message = `Sayın ${customer.adSoyad}, ${Math.abs(finalBalance).toFixed(2)} TL tutarında bakiyeniz bulunmaktadır. Bilgilerinize.`;
      const sendSms = httpsCallable(functions, 'sendMaintenanceSms');
      await sendSms({ phone: customer.telefon, message });
      alert('SMS başarıyla gönderildi!');
    } catch (error) {
      console.error('SMS gönderim hatası:', error);
      alert('SMS gönderilemedi: ' + error.message);
    } finally {
      setIsSendingSms(false);
    }
  };

  if (loading) { return <Loading message="Hesap ekstresi yükleniyor..." />; }
  if (!customer) { return <Typography variant="h5" align="center" sx={{ mt: 4 }}>Müşteri Bulunamadı.</Typography>; }

  return (
    <>
      <AddDebtModal
        isOpen={isDebtModalOpen}
        onClose={() => setIsDebtModalOpen(false)}
        onSave={handleSaveDebt}
        customerName={customer.adSoyad}
      />
      <AddPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSave={handleSaveGeneralPayment}
        customerName={customer.adSoyad}
        kalanTutar={finalBalance}
        openRecords={openRecords}
      />
      <PlanlanmisOdemeModal isOpen={isPlanlamaModalOpen} onClose={() => setIsPlanlamaModalOpen(false)} customer={customer} finalBalance={finalBalance} />

      <Box className="printable-area">
        <GlassCard sx={{ mb: 3, p: 2 }} className="no-print">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" component="h1" fontWeight="bold">Hesap Ekstresi</Typography>
            <Box>
              <Button component={RouterLink} to={`/musteri/${customer.id}`} variant="outlined" startIcon={<PersonIcon />} sx={{ mr: 1 }}>Müşteri Detayına Git</Button>
              <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} sx={{ mr: 1 }} onClick={() => setIsPlanlamaModalOpen(true)}>Planlanmış Tahsilat Ekle</Button>
              <Button variant="outlined" color="error" startIcon={<CreditCardOffIcon />} sx={{ mr: 1 }} onClick={() => setIsDebtModalOpen(true)}>Borç Ekle</Button>
              <Button variant="outlined" color="success" startIcon={<AddCardIcon />} sx={{ mr: 1 }} onClick={() => setIsPaymentModalOpen(true)}>Tahsilat Ekle</Button>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ mr: 1 }}>Yazdır</Button>
              <Button variant="contained" color="success" startIcon={<FileDownloadIcon />} onClick={handleExcelExport} disabled={!allMovements || allMovements.length === 0}>Excel</Button>
              <Button
                variant="outlined"
                color="info"
                size="small"
                startIcon={<SmsIcon />}
                onClick={handleSendSmsReminder}
                disabled={isSendingSms || finalBalance <= 0}
                sx={{ ml: 1 }}
              >
                {isSendingSms ? 'Gönderiliyor...' : 'SMS Hatırlat'}
              </Button>
            </Box>
          </Box>
        </GlassCard>

        <AccountSummaryHeader customer={customer} finalBalance={finalBalance} />

        <AccountStatementTable
          allMovements={allMovements}
          handleDeleteTransaction={handleDeleteTransaction}
        />

        <ScheduledPaymentsTable
          pendingScheduledPayments={pendingScheduledPayments}
          handleDeleteScheduledPayment={handleDeleteScheduledPayment}
        />
      </Box>
    </>
  );
};

export default CariHesapEkstresi;